import { useState, useEffect } from "react";
import { NEW_LOGIN_REACHIQ_LOGO } from "../../utils/constants/assets";
import CircularLoader from "../../components/Loaders/CircularLoader";
import { PRIMARY, HELP, PURPLE, SUCCESS } from "../../utils/constants/colors";
import "./onboarding.css";

// The four ReachIQ pillars, shown as module "pills" that light up one-by-one while
// the workspace is provisioned. Mirrors Step 5 of the onboarding prototype.
const MODULES = [
  {
    id: "m0",
    label: "Prospect Discovery",
    iconBg: PRIMARY.W_50,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke={PRIMARY.W_500} strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" focusable="false">
        <circle cx="11" cy="11" r="7" />
        <path d="M16 16l5 5" />
      </svg>
    ),
  },
  {
    id: "m1",
    label: "Marketing Lists",
    iconBg: HELP.W_50,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke={HELP.W_500} strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" focusable="false">
        <line x1="8" y1="6" x2="21" y2="6" />
        <line x1="8" y1="12" x2="21" y2="12" />
        <line x1="8" y1="18" x2="21" y2="18" />
        <line x1="3" y1="6" x2="3.01" y2="6" />
        <line x1="3" y1="12" x2="3.01" y2="12" />
        <line x1="3" y1="18" x2="3.01" y2="18" />
      </svg>
    ),
  },
  {
    id: "m2",
    label: "Campaign Engine",
    iconBg: PURPLE.W_50,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke={PURPLE.W_600} strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" focusable="false">
        <path d="M22 2L11 13" />
        <path d="M22 2L15 22 11 13 2 9l20-7z" />
      </svg>
    ),
  },
  {
    id: "m3",
    label: "CRM & Contacts",
    iconBg: SUCCESS.W_50,
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke={SUCCESS.W_500} strokeWidth="2.5" strokeLinecap="round" aria-hidden="true" focusable="false">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
  },
];

const STEPS_TEXT = [
  "Setting up prospect discovery tools…",
  "Configuring marketing list builder…",
  "Preparing campaign engine…",
  "Loading CRM & contact management…",
  "Your workspace is ready!",
];

const STEP_PCTS = [0, 26, 52, 76, 100];

// Used purely as a loading VISUAL (e.g. inside EmailVerification while it verifies
// the token). The host screen owns navigation, so this never redirects on its own —
// it just animates the modules + progress bar and settles on "ready".
const WorkspacePreparationScreen = () => {
  const [activeModules, setActiveModules] = useState([]);
  const [stepIdx, setStepIdx]             = useState(0);
  const [barWidth, setBarWidth]           = useState(0);

  useEffect(() => {
    let cancelled = false;

    const advance = (idx) => {
      if (cancelled) return;
      if (idx >= MODULES.length) {
        // Final "ready" state: bar full, last step text. No redirect.
        setBarWidth(100);
        setStepIdx(STEPS_TEXT.length - 1);
        return;
      }
      setBarWidth(STEP_PCTS[idx + 1]);
      setStepIdx(idx);
      setActiveModules((prev) => [...prev, MODULES[idx].id]);
      setTimeout(() => advance(idx + 1), 750);
    };

    const start = setTimeout(() => advance(0), 600);
    return () => {
      cancelled = true;
      clearTimeout(start);
    };
  }, []);

  return (
    <div className="onb-wsp-outer">
      {/* Brand — real ReachIQ logo (icon + wordmark). */}
      <div className="onb-wsp-brand">
        <img src={NEW_LOGIN_REACHIQ_LOGO} className="onb-wsp-logo" alt="ReachIQ" />
      </div>

      {/* Spinner — DS CircularLoader. aria-hidden so its inner MUI role=progressbar
          rings don't compete with the real progress bar below. */}
      <div className="onb-wsp-ring" aria-hidden="true">
        <CircularLoader size={56} aria-hidden="true" />
      </div>

      {/* Module pills — light up one by one as each comes online. */}
      <div className="onb-wsp-mods">
        {MODULES.map((mod) => (
          <div
            key={mod.id}
            className={`onb-wsp-mod ${activeModules.includes(mod.id) ? "on" : ""}`}
          >
            <div className="onb-wsp-mod-ic" style={{ background: mod.iconBg }}>
              {mod.icon}
            </div>
            {mod.label}
          </div>
        ))}
      </div>

      <h2 className="onb-wsp-h">Preparing Your ReachIQ Workspace</h2>
      <p className="onb-wsp-p">
        We&rsquo;re setting up your prospect discovery tools, marketing list builder,
        campaign engine, and CRM: everything you need to run outbound sales from one
        place.
      </p>

      {/* Progress bar */}
      <div
        className="onb-wsp-prog"
        role="progressbar"
        aria-valuenow={barWidth}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Workspace setup progress"
      >
        <div className="onb-wsp-fill" style={{ width: `${barWidth}%` }} />
      </div>

      <div className="onb-wsp-stxt" role="status" aria-live="polite">{STEPS_TEXT[stepIdx]}</div>
    </div>
  );
};

export default WorkspacePreparationScreen;
